import React, { useRef, useState } from "react";
import {
	Button,
	Text,
	Link,
	Form,
	Select,
} from "@website-builder/ui/shared/elements";
import { PRODUCTION_API_URL } from "@website-builder/ui/shared/constants";
import { formatColor } from "@website-builder/utilities/utils/DesignUtils.js";
import { GetReferrer } from "@website-builder/utilities/utils/GetReferrer.js";
import {
	isBusinessVerticalB2U,
	getTimeStamp,
} from "@website-builder/utilities/utils/helpers.js";
import {
	StagedFormWrapper,
	ButtonWrapper,
	ProgressBar,
	Headline,
	ProgressBarWrapper,
	BackButtonWrapper,
} from "./styles";
import {
	callEventForStagedForm,
	getFormStyles,
	modifyFormValuesForB2U,
} from "../utils";
import {
	callAppStartedAPI,
	callInterestedAPI,
	sendToHubspot,
} from "../apiHandlers";
import {
	AC_LEAD_TYPE_VALUE,
	HUBSPOT_DATA_EVENTS,
	STAGED_FORM_BG_TYPES,
} from "../constants";
import HSForm from "../HSForm";
import {
	callIdentifyEvent,
	callB2CIdentifyEvent,
} from "@website-builder/utilities/utils/utils.js";

const isB2U = isBusinessVerticalB2U();
const isProductionBuild = process.env.GATSBY_ENVIRONMENT === "production";
const StagedForm = ({
	formDetails = {},
	context = {},
	pageSection = "",
	submitEvent = HUBSPOT_DATA_EVENTS.LEAD_FORM_SUBMIT,
	hasCourseSelector = false,
}) => {
	const [stage, setStage] = useState(1);
	const [userEmail, setUserEmail] = useState("");
	const [allFormValues, setAllFormValues] = useState({});
	const [disableSubmit, setDisableSubmit] = useState(false);
	const [program, setProgram] = useState(null);
	const [courseContextFields, setCourseContextFields] = useState({
		workshop_id: context?.workshop_id,
		dripTagPrefix: context?.dripTagPrefix,
	});
	const [dropdownError, setDropdownError] = useState({
		program: "Choose an option",
	});
	const [showErrors, setShowErrors] = useState(false);
	// content reference to enable scroll back to top
	const contentReference = useRef(null);

	const { apiBaseUrl } = context;
	const {
		headline,
		progressCopy,
		redirectLink = {},
		styleConfig = {},
		consentText = "",
		consentChecked = false,
	} = formDetails;

	let formGroups, dropdownItems, MAX_STAGE;
	const formIDsObj =
		isProductionBuild || isB2U
			? formDetails?.formIDs
			: formDetails?.testFormIDs;
	const [formIDs, setFormIDs] = useState(
		hasCourseSelector ? [] : formIDsObj?.map((formID) => formID?.text),
	);

	if (hasCourseSelector) {
		formGroups =
			isProductionBuild || isB2U
				? formDetails?.formGroups
				: formDetails?.testFormGroups;

		dropdownItems = formGroups?.map((formGroup) => formGroup?.dropdownName);

		MAX_STAGE =
			1 +
			Math.max(...formGroups.map((formGroup) => formGroup?.formIDs?.length));
		// Math.max takes props individually and not in array
		if (MAX_STAGE === 1) {
			console.error("No form groups provided for staged form.");
		}
	} else {
		MAX_STAGE = formIDsObj?.length;
		if (!MAX_STAGE) {
			console.error("No form IDs provided for staged form.");
		}
	}

	const contextWithCourseData = {
		...context,
		...courseContextFields,
	};

	const {
		formBg = STAGED_FORM_BG_TYPES.LIME_GREEN,
		buttonConfig: [buttonConfig = {}] = [],
		linkColor = {},
		hasDarkBackground,
		formBackgroundColor,
	} = styleConfig[0] || {};

	const fieldsForFormstyles = {
		formBg,
		formBackgroundColor,
		hasDarkBackground,
	};

	const formStyles = getFormStyles(isB2U, fieldsForFormstyles);

	const apiBaseUrlString = isB2U ? PRODUCTION_API_URL : apiBaseUrl;

	const scrollIntoView = () => {
		contentReference?.current?.scrollIntoView({
			behavior: "smooth",
			block: "center",
		});
	};

	const handleCourseSelection = (target, event) => {
		event.preventDefault();
		const value = target?.target?.value;
		!!value ? setProgram(value) : setProgram(null);
		const selectedFormGroup = formGroups?.find(
			(formGroup) => formGroup?.dropdownName === value,
		);
		const selectedCourseFormIDs = selectedFormGroup?.formIDs || [];
		const sanitisedIDs = selectedCourseFormIDs.map((formID) => formID?.text);
		setFormIDs(sanitisedIDs);
		setCourseContextFields({
			workshop_id: selectedFormGroup?.workshopId,
			dripTagPrefix: selectedFormGroup?.dripTagPrefix,
		});
	};

	const handleFormSubmit = (formValues) => {
		scrollIntoView();
		const [modifiedFormValues, activeCampaignFieldValues] = isB2U
			? modifyFormValuesForB2U(formValues)
			: [formValues, {}];

		if (modifiedFormValues?.email) {
			setUserEmail(modifiedFormValues?.email);
		} else if (userEmail) {
			modifiedFormValues.email = userEmail;
		}

		const accumulatedFormValues = { ...allFormValues, ...modifiedFormValues };
		setAllFormValues(accumulatedFormValues);

		const eventData = !isB2U
			? createEventData(modifiedFormValues)
			: createB2UEventData(accumulatedFormValues);

		//Set contact in window on the last step
		if (eventData?.setWindowContacts) {
			window.contacts = eventData.contact;
		}

		executeAPICallsAndRedirect(
			modifiedFormValues,
			accumulatedFormValues,
			activeCampaignFieldValues,
			eventData,
		);

		if (stage < MAX_STAGE) {
			setStage(stage + 1);
		}
		return true;
	};

	const createEventData = (formValues) => {
		const [referral, referral2] = GetReferrer();
		const eventData = {
			step: stage,
			...formValues,
			page_section: pageSection,
			referral,
			referral2,
			workshop_id: courseContextFields?.workshop_id,
			form_id: formIDs[stage - 1],
		};
		if (formValues?.firstname || formValues?.lastname) {
			eventData.name = `${formValues?.firstname} ${formValues?.lastname}`;
		}

		return eventData;
	};

	const createB2UEventData = (allFormValues) => {
		const [referral, referral2] = GetReferrer();
		const isFinalStage = stage === MAX_STAGE;
		return {
			eventName: isFinalStage
				? "Lead Form Submitted"
				: "Lead Form Partial Submit",
			track: {
				course_slug: allFormValues?.preferred_course || context?.context?.slug,
				referral,
				referral2,
				lead_type: isFinalStage ? "LFS" : "LFPS",
				category: "Form Submission",
				hit_timestamp: getTimeStamp(),
				container_ID: process.env.GATSBY_GTM_CONTAINER_ID,
				label: decodeURIComponent(window.location.pathname),
				variant_id: allFormValues?.variationid,
				form_id: formIDs[stage - (hasCourseSelector ? 2 : 1)],
				step: stage,
				workshop_id: courseContextFields?.workshop_id,
			},
			contact: {
				email: allFormValues?.email,
				firstname: allFormValues?.firstname,
				lastname: allFormValues?.lastname,
				phone: allFormValues?.phone, //phone will be undefined in the first stage
			},
			setWindowContacts: isFinalStage, //set window.contacts only on last stage
		};
	};

	const executeAPICallsAndRedirect = (
		formValues,
		accumulatedFormValues,
		activeCampaignFieldValues,
		eventData,
	) => {
		const currentFormID = formIDs[stage - (hasCourseSelector ? 2 : 1)];
		const functionCalls = [];

		//disable submit button on last stage after first click
		if (stage === MAX_STAGE) {
			setDisableSubmit(true);
		} else {
			setDisableSubmit(false);
		}

		//send lead params lke uuid, utm info, gclid etc. only for b2c forms in stage 1
		//also call identify step only for b2c forms in stage 1
		const isB2CFirstStep =
			!isB2U &&
			((stage === 1 && !hasCourseSelector) ||
				(stage === 2 && hasCourseSelector));

		//send data to hubspot on each stage
		functionCalls.push(
			sendToHubspot(
				apiBaseUrlString,
				contextWithCourseData,
				currentFormID,
				formValues,
				isB2CFirstStep,
			),
		);

		const b2cApiCalls = {
			1: callAppStartedAPI, //send app started lead to marketo on first stage as we get the email here
		};
		b2cApiCalls[MAX_STAGE] = callInterestedAPI; //send applied lead to marketo on last step as user completes the form here

		if (!isB2U && b2cApiCalls[stage - (hasCourseSelector ? 1 : 0)]) {
			functionCalls.push(
				b2cApiCalls[stage - (hasCourseSelector ? 1 : 0)](
					apiBaseUrlString,
					contextWithCourseData,
					currentFormID,
					accumulatedFormValues,
				),
			);
		}

		const getSubmitRedirectLink = () => {
			if (hasCourseSelector) {
				return formGroups?.find(
					(formGroup) => formGroup?.dropdownName === program,
				)?.redirectLink;
			} else {
				return redirectLink;
			}
		};

		const redirectConfig =
			stage === MAX_STAGE
				? {
						actionOnSubmit: "redirect",
						submitRedirectLink: getSubmitRedirectLink(),
						pageSection,
					}
				: {};

		if (functionCalls.length) {
			isB2U && callIdentifyEvent(eventData?.track);
			isB2CFirstStep &&
				formValues?.email &&
				callB2CIdentifyEvent(formValues?.email);
			Promise.all(functionCalls)
				.then((res) => {
					callEventForStagedForm(
						submitEvent || HUBSPOT_DATA_EVENTS.FORM_SUBMIT,
						eventData,
						redirectConfig,
					);
				})
				.catch((err) => {
					if (isB2U) {
						submitEvent = HUBSPOT_DATA_EVENTS.LEAD_FORM_SUBMIT_FAIL;
					}
					console.error("Error: ", err);
					callEventForStagedForm(
						submitEvent
							? HUBSPOT_DATA_EVENTS.LEAD_FORM_SUBMIT_FAIL
							: HUBSPOT_DATA_EVENTS.FORM_SUBMIT_FAIL,
						eventData,
						redirectConfig,
					);
				});
		}
	};

	const handleBackButtonClick = () => {
		if (stage === 1) return;
		setStage(stage - 1);
	};

	const submitButtonJSX = () => {
		let buttonText = "Next";

		if (stage === MAX_STAGE) {
			buttonText = "Submit";
		}
		return (
			<ButtonWrapper>
				<Button
					type="submit"
					buttonText={buttonText}
					className="submit-button"
					darkBg={formStyles.darkBg}
					buttonConfiguration={buttonConfig}
					disabled={disableSubmit}
				/>
			</ButtonWrapper>
		);
	};

	const backButtonJSX = () => {
		if (stage === 1) return null;
		const backLinkColor = isB2U ? formatColor(linkColor) : formStyles.linkColor;
		return (
			<BackButtonWrapper linkColor={backLinkColor}>
				<Link
					iconConfig={{ iconName: "leftArrow" }}
					onClick={handleBackButtonClick}
					linkText="Back"
					className="back-button"
					darkBg={formStyles.darkBg}
				>
					<Text variant="paragraph_S_bold" className="back-link-text">
						Back
					</Text>
				</Link>
			</BackButtonWrapper>
		);
	};

	const stageNumberBufferValue = hasCourseSelector ? 2 : 1;

	return (
		<StagedFormWrapper
			formStyles={formStyles}
			isB2U={isB2U}
			ref={contentReference}
			className="staged-form-wrapper"
		>
			<Headline variant="headline_5" className="stage-form-texts">
				{headline}
			</Headline>
			<ProgressBarWrapper>
				<Text variant="paragraph_S_bold" className="stage-form-texts">
					{progressCopy}
				</Text>
				<ProgressBar color={formStyles.prgressBarBg}>
					<div
						className="progress"
						style={{ width: `${(stage / MAX_STAGE) * 100}%` }}
					/>
				</ProgressBar>
				<Text className="stage-form-texts">
					Step {stage} of {MAX_STAGE}
				</Text>
			</ProgressBarWrapper>

			{hasCourseSelector ? (
				<Form
					className="staged-form-course-selector"
					style={{ display: stage === 1 ? "block" : "none" }}
					onSubmit={(e) => {
						e.preventDefault();
					}}
				>
					<Select
						name="program"
						label={formDetails?.courseSelectorLabel || "Select your program"}
						placeholder="Please select..."
						required
						showErrors={showErrors}
						value={program}
						error={[dropdownError, setDropdownError]}
						onChange={(target, event) => {
							handleCourseSelection(target, event);
						}}
					>
						{dropdownItems?.map((dropdownItem, index) => (
							<option key={index} value={dropdownItem} id={index}>
								{dropdownItem}
							</option>
						))}
					</Select>

					<ButtonWrapper>
						<Button
							buttonText="Next"
							className="submit-button"
							darkBg={formStyles.darkBg}
							buttonConfiguration={buttonConfig}
							onClick={() => {
								if (dropdownError["program"]) {
									setShowErrors(true);
									return;
								}
								setStage(2);
							}}
						/>
					</ButtonWrapper>
				</Form>
			) : null}

			{formIDs?.map((formID, index) => (
				<div
					className={`staged-form stage-${index + stageNumberBufferValue}-form`}
					style={{
						display:
							stage === index + stageNumberBufferValue ? "block" : "none",
					}}
					key={`div-form-${index}`}
				>
					<HSForm
						formID={formID}
						context={contextWithCourseData}
						onSubmit={handleFormSubmit}
						takeUserConsent={index === MAX_STAGE - (hasCourseSelector ? 2 : 1)}
						consentText={consentText}
						darkBg={formStyles.darkBg}
						key={`form-${index}`}
						consentChecked={consentChecked}
					>
						{submitButtonJSX()}
						{backButtonJSX()}
					</HSForm>
				</div>
			))}
		</StagedFormWrapper>
	);
};

export default StagedForm;
